import { CdkDragEnd, CdkDragMove, CdkDragStart } from '@angular/cdk/drag-drop';
import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Reservation, Shift, Table, TableSlot } from '@model/reservation';
import { ClickEvent, DragEndEvent, DragMoveEvent, DragStartEvent, TablesMode } from '@model/tables-plan';
import { Store, select } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { TablesPlanActions } from '@store/actions/tables-plan.actions';
import { State } from '@store/index';
import * as ReservationListSelectors from '@store/selectors/reservation-list.selectors';
import * as TablesPlanSelectors from '@store/selectors/tables-plan.selectors';
import { Observable, Subject, takeUntil } from 'rxjs';
import { Common } from 'src/app/lib/common-methods';
import { ReservationService } from 'src/app/lib/reservation.service';

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrl: './table.component.scss'
})
export class TableComponent implements OnInit, OnDestroy {

  constructor(
    private translate: TranslateService,
    private reservationService: ReservationService,
    private store: Store<State>,
  ) {

  }

  destroy$: Subject<boolean> = new Subject<boolean>();

  editMode$: Observable<boolean> = this.store.pipe(select(TablesPlanSelectors.selectEditMode));
  editMode: boolean = false;
  showSurname$: Observable<boolean> = this.store.pipe(select(TablesPlanSelectors.selectShowSurname));
  showSurname: boolean = false;
  showProgress$: Observable<boolean> = this.store.pipe(select(TablesPlanSelectors.selectShowProgress));
  showProgress: boolean = false;
  editingTable$: Observable<{ table: Table, isNew: boolean } | null> = this.store.pipe(select(TablesPlanSelectors.selectEditingTable));
  editingTable: { table: Table, isNew: boolean } | null = null;
  zoom$: Observable<number> = this.store.pipe(select(TablesPlanSelectors.selectZoom));
  zoom: number = 1;
  selectedShift$: Observable<Shift | null | undefined> = this.store.pipe(select(ReservationListSelectors.selectSelectedShift));
  selectedShift: Shift | null | undefined = null;
  reservationAssignTablesMode$: Observable<boolean> = this.store.pipe(select(TablesPlanSelectors.selectReservationAssignTablesMode));
  reservationAssignTablesMode: boolean = false;
  tablesMode$: Observable<TablesMode> = this.store.pipe(select(TablesPlanSelectors.selectTablesMode));
  tablesMode: TablesMode = 'Tables';
  assignTablesReservation$: Observable<Reservation | null> = this.store.pipe(select(TablesPlanSelectors.selectAssignTablesReservation));
  assignTablesReservation: Reservation | null = null;
  conflictingTables$: Observable<Table[]> = this.store.pipe(select(TablesPlanSelectors.selectConflictingTables));
  conflictingTables: Table[] = [];

  @ViewChild('tableRef') tableRef!: ElementRef;
  get tableRect(): DOMRect {
    return this.tableRef.nativeElement.getBoundingClientRect();
  }

  @ViewChild('tableImageRef') tableImageRef!: ElementRef;
  get tableImageRect(): DOMRect {
    return this.tableImageRef.nativeElement.getBoundingClientRect();
  }

  @ViewChild('timeslot1') timeslot1Ref!: ElementRef;
  get timeslot1Rect(): DOMRect {
    return this.timeslot1Ref?.nativeElement?.getBoundingClientRect() ?? {};
  }
  @ViewChild('timeslot2') timeslot2Ref!: ElementRef;
  get timeslot2Rect(): DOMRect {
    return this.timeslot2Ref?.nativeElement?.getBoundingClientRect() ?? {};
  }
  @ViewChild('timeslot3') timeslot3Ref!: ElementRef;
  get timeslot3Rect(): DOMRect {
    return this.timeslot3Ref?.nativeElement?.getBoundingClientRect() ?? {};
  }

  @Input() table: Table | null = null;
  @Input() selected: boolean = false;
  @Input() set reservations(value: Reservation[]) {
    this.tableReservations = value
      .filter(r => Common.tableReservationsFilter(this.table, r))
      .filter(r => Common.completedReservationsFilter(r))
      .sort(Common.arrivalTimeComparator);

    this.refreshConflictingReservations();

    this.updateSlots();
  }
  tableReservations: Reservation[] = [];
  conflictingReservations: Reservation[] = [];

  @Output() onClick: EventEmitter<ClickEvent> = new EventEmitter();
  @Output() onDragStart: EventEmitter<DragStartEvent> = new EventEmitter();
  @Output() onDragMove: EventEmitter<DragMoveEvent> = new EventEmitter();
  @Output() onDragEnd: EventEmitter<DragEndEvent> = new EventEmitter();

  @HostListener('document:touchstart', ['$event'])
  handleTouchStart(event: TouchEvent) {

    if (!this.tableRef.nativeElement.contains(event.target)) {
      return;
    }

    this.tableMouseDown(event);
  }

  @HostListener('document:touchend', ['$event'])
  handleTouchEnd(event: TouchEvent) {

    if (!this.tableRef.nativeElement.contains(event.target)) {
      return;
    }

    this.tableMouseUp(event);
  }

  get tableInfoRows(): { customerName: string, covers: number }[] {

    const tableReservations: Reservation[] = [];
    if (this.slot1?.reservation != null) {
      tableReservations.push(this.slot1.reservation);
    }
    if (this.slot2?.reservation != null) {
      tableReservations.push(this.slot2.reservation);
    }
    if (this.slot3?.reservation != null) {
      tableReservations.push(this.slot3.reservation);
    }

    return tableReservations.map(r => {
      return { customerName: this.reservationService.getCustomerName(r), covers: r.reservationDetails.covers };
    });
  }

  slot1: TableSlot = { slotNumber: 1, reservation: null };
  slot2: TableSlot = { slotNumber: 2, reservation: null };
  slot3: TableSlot = { slotNumber: 3, reservation: null };

  dragging: boolean = false;
  dndEnabled: boolean = false;

  ngOnInit(): void {

    this.updateSlots();

    this.editMode$.pipe(takeUntil(this.destroy$)).subscribe((editMode: boolean) => {
      this.editMode = editMode;
    });

    this.showSurname$.pipe(takeUntil(this.destroy$)).subscribe((showSurname: boolean) => {
      this.showSurname = showSurname;
    });

    this.showProgress$.pipe(takeUntil(this.destroy$)).subscribe((showProgress: boolean) => {
      this.showProgress = showProgress;
    });

    this.zoom$.pipe(takeUntil(this.destroy$)).subscribe((zoom: number) => {
      this.zoom = zoom;
    });

    this.selectedShift$.pipe(takeUntil(this.destroy$)).subscribe((selectedShift: Shift | null | undefined) => {
      this.selectedShift = selectedShift;
      this.updateSlots();
    });

    this.editingTable$.pipe(takeUntil(this.destroy$)).subscribe((editingTable) => {
      this.editingTable = editingTable;
    });

    this.reservationAssignTablesMode$.pipe(takeUntil(this.destroy$)).subscribe((reservationAssignTablesMode) => {
      this.reservationAssignTablesMode = reservationAssignTablesMode;
    });

    this.conflictingTables$.pipe(takeUntil(this.destroy$)).subscribe((conflictingTables) => {
      this.conflictingTables = conflictingTables;
    });

    this.tablesMode$.pipe(takeUntil(this.destroy$)).subscribe((tablesMode) => {
      this.tablesMode = tablesMode;
    });

    this.assignTablesReservation$.pipe(takeUntil(this.destroy$)).subscribe((assignTablesReservation) => {
      this.assignTablesReservation = assignTablesReservation;
      this.refreshConflictingReservations();
    });

  }

  refreshConflictingReservations() {
    if (this.assignTablesReservation == null || this.tableReservations.length <= 0) {
      this.conflictingReservations = [];
      return;
    }
    this.conflictingReservations = this.reservationService.getConflictingReservationsUsingDuration(this.assignTablesReservation, this.tableReservations);

    if (this.conflictingReservations.length > 0) {
      if (!this.conflictingTables.some(t => t.tableName === this.table?.tableName)) {
        this.store.dispatch({ type: TablesPlanActions.addConflictingTable.type, table: this.table });
      }
    } else {
      if (this.conflictingTables.some(t => t.tableName === this.table?.tableName)) {
        this.store.dispatch({ type: TablesPlanActions.removeConflictingTable.type, table: this.table });
      }
    }
  }

  tableClicked(event: MouseEvent) {
    event?.stopPropagation();
    if (this.dragging) {
      return;
    }
    this.onClick.emit({ event, table: this.table as Table });
    this.dndEnabled = false;
  }

  tableMouseDownTimeout: NodeJS.Timeout | undefined;
  tableMouseDown(event: MouseEvent | TouchEvent) {
    if (this.editMode) {
      this.tableMouseDownTimeout = setTimeout(() => {
        this.dndEnabled = true;
      }, 100);
    }

    this.dragging = false;
  }

  tableMouseUp(event: MouseEvent | TouchEvent) {
    if (this.tableMouseDownTimeout != null) {
      clearTimeout(this.tableMouseDownTimeout);
    }
    this.dndEnabled = false;
  }

  dragStarted(event: CdkDragStart) {
    this.dragging = true;
    this.onDragStart.emit({ event, table: this.table as Table });
  }

  dragMoved(event: CdkDragMove) {

    // drag n drop fix when zoom != 1
    this.tableRef.nativeElement.style.setProperty('transform', `translate3d(${event.distance.x * (1 / this.zoom)}px, ${event.distance.y * (1 / this.zoom)}px, 0px)`);

    this.onDragMove.emit({ event, table: this.table as Table });
  }

  dragEnded(event: CdkDragEnd) {
    this.onDragEnd.emit({ event, table: this.table as Table });
    this.dndEnabled = false;
  }

  updateSlots() {

    const slots = this.reservationService.getTableSlots(this.tableReservations, this.selectedShift);

    this.slot1 = slots.slot1;
    this.slot2 = slots.slot2;
    this.slot3 = slots.slot3;
  }

  resetSlots() {
    this.slot1.reservation = null;
    this.slot2.reservation = null;
    this.slot3.reservation = null;
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

}
